import React, { useState, useContext } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeProvider } from '@mui/material/styles';
import appLogo from '../../assets/svg/app-logo.svg';
import { FirebaseAuthContext } from '../../auth/FirebaseAuth';
import { lightTheme } from '../../theme/createAppTheme';
import useTranslation from '../../translations/use-translation';
import './CSCOwnerPortal.styles.scss';

const NAV_ITEMS = [
  { key: 'dashboard',   label: 'CSC_DASHBOARD',   icon: <DashboardOutlinedIcon />,   path: '/csc-portal' },
  { key: 'profile',     label: 'CSC_MY_PROFILE',   icon: <PersonOutlineIcon />,       path: '/csc-portal/profile' },
  { key: 'add-service', label: 'CSC_ADD_SERVICE',  icon: <AddCircleOutlineIcon />,    path: '/csc-portal/add-service' },
  { key: 'services',    label: 'CSC_MY_SERVICES',  icon: <ListAltOutlinedIcon />,     path: '/csc-portal/services' },
  { key: 'requests',    label: 'CSC_REQUESTS',     icon: <InboxOutlinedIcon />,       path: '/csc-portal/requests' },
  { key: 'categories', label: 'CSC_MY_CATEGORIES', icon: <CategoryOutlinedIcon />,   path: '/csc-portal/categories' },
];

const CSCOwnerPortal = () => {
  const translate = useTranslation();
  const navigate = useNavigate();
  const { logOutUser } = useContext(FirebaseAuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const profile = (() => {
    try { return JSON.parse(localStorage.getItem('cscOwnerProfile')) || {}; }
    catch { return {}; }
  })();

  const initials = (profile.ownerName || 'C')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleLogout = async () => {
    try { await logOutUser(); } catch (_) {}
    localStorage.removeItem('cscOwnerLoggedIn');
    localStorage.removeItem('cscOwnerProfile');
    navigate('/');
  };

  return (
    <ThemeProvider theme={lightTheme}>
    <div className="cop">

      {/* ── Sidebar ── */}
      <aside className={`cop__sidebar${sidebarOpen ? ' cop__sidebar--open' : ''}`}>
        <div className="cop__sidebar-logo">
          <img src={appLogo} alt="Online Applications" className="cop__sidebar-logo-img" />
          <span className="cop__sidebar-logo-name">{translate('CSC_OWNER_PORTAL')}</span>
        </div>

        <nav className="cop__nav">
          {NAV_ITEMS.map(({ key, label, icon, path }) => (
            <NavLink
              key={key}
              to={path}
              end={path === '/csc-portal'}
              className={({ isActive }) =>
                `cop__nav-item${isActive ? ' cop__nav-item--active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="cop__nav-icon">{icon}</span>
              <span className="cop__nav-label">{translate(label)}</span>
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          className="cop__nav-logout"
          onClick={handleLogout}
        >
          <span className="cop__nav-icon"><LogoutOutlinedIcon /></span>
          <span className="cop__nav-label">{translate('CSC_LOGOUT')}</span>
        </button>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="cop__overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Main area ── */}
      <div className="cop__main">

        {/* Top bar */}
        <header className="cop__topbar">
          <IconButton
            className="cop__hamburger"
            onClick={() => setSidebarOpen((s) => !s)}
            aria-label="Toggle menu"
            size="small"
          >
            {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <div className="cop__topbar-right">
            <div className="cop__topbar-user">
              <Avatar className="cop__topbar-avatar">{initials}</Avatar>
              <div className="cop__topbar-info">
                <span className="cop__topbar-name">{profile.ownerName || translate('CSC_OWNER')}</span>
                <span className="cop__topbar-email">{profile.email || ''}</span>
              </div>
            </div>

            <Tooltip title={translate('CSC_LOGOUT')} arrow>
              <IconButton
                className="cop__topbar-logout"
                onClick={handleLogout}
                aria-label="Logout"
                size="small"
              >
                <LogoutOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </header>

        {/* Page content */}
        <main className="cop__content">
          <Outlet />
        </main>
      </div>
    </div>
    </ThemeProvider>
  );
};

export default CSCOwnerPortal;
